import { QueryState } from '@sentinel/web-core';
import { Icon } from '@sentinel/ui';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Notification } from '@sentinel/shared';
import { NotificationItem } from './NotificationItem';
import { useNotifications } from './use-notifications';

const K = 'shell.notifications';

/** The bell of the topbar (CLAUDE.md §23): how many are unread, as a number and a word
 *  (rule 13), and the latest ones. Opening one marks it read and goes where it points. Only
 *  inside an organization: notifications belong to one. */
export function NotificationBell({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { list, markRead, markAllRead } = useNotifications(enabled);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current !== null && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [open]);

  if (!enabled) return null;
  const unread = list.data?.unread ?? 0;
  const openOne = (n: Notification) => {
    if (n.readAt === null) markRead.mutate(n.id);
    setOpen(false);
    if (n.link !== null) void navigate({ to: n.link });
  };

  return (
    <div className="msel" ref={ref}>
      <button
        type="button"
        className="btn ico ghost ntf-btn"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={t(`${K}.open`, { count: unread })}
        title={t(`${K}.title`)}
      >
        <Icon name="bell" size={18} />
        {unread > 0 ? (
          <span className="ntf-count">{t(`${K}.badge`, { count: unread })}</span>
        ) : null}
      </button>
      {open ? (
        <div className="msel-menu ntf-menu" style={{ left: 'auto', right: 0 }} role="menu">
          <div className="ntf-head">
            <b>{t(`${K}.title`)}</b>
            {unread > 0 ? (
              <button
                type="button"
                className="btn sm"
                disabled={markAllRead.isPending}
                onClick={() => markAllRead.mutate()}
              >
                {t(`${K}.markAll`)}
              </button>
            ) : null}
          </div>
          <QueryState query={list} skeletonLines={3}>
            {(data) =>
              data.items.length === 0 ? (
                <p className="note" style={{ padding: '8px 9px' }}>
                  {t(`${K}.empty`)}
                </p>
              ) : (
                data.items.map((n) => (
                  <NotificationItem key={n.id} notification={n} onOpen={openOne} />
                ))
              )
            }
          </QueryState>
        </div>
      ) : null}
    </div>
  );
}
